import * as XLSX from 'xlsx'
import { supabase } from './supabase'

export type ParsedRow = Record<string, string | number | null>

export interface ImportFieldMap {
  sku: string
  name: string
  current_stock: string
  cost?: string
  monthly_avg_manual?: string
  sale_price?: string
  supplier_name?: string
}

export interface ImportError {
  row: number
  sku: string
  reason: string
}

export interface ImportResult {
  total: number
  inserted: number
  updated: number
  skipped: number
  errors: ImportError[]
}

export interface PreviewResult {
  columns: string[]
  rows: ParsedRow[]
  totalRows: number
}

const PREVIEW_LIMIT = 5
const BATCH_SIZE = 200

async function readWorkbook(file: File): Promise<XLSX.WorkBook> {
  const buf = await file.arrayBuffer()
  return XLSX.read(buf, { type: 'array' })
}

function firstSheetRows(wb: XLSX.WorkBook): ParsedRow[] {
  const sheetName = wb.SheetNames[0]
  if (!sheetName) return []
  const sheet = wb.Sheets[sheetName]
  return XLSX.utils.sheet_to_json<ParsedRow>(sheet, { defval: null, raw: true })
}

export async function parseFilePreview(file: File): Promise<PreviewResult> {
  const wb = await readWorkbook(file)
  const rows = firstSheetRows(wb)
  const columns = rows.length > 0 ? Object.keys(rows[0]) : []
  return {
    columns,
    rows: rows.slice(0, PREVIEW_LIMIT),
    totalRows: rows.length,
  }
}

function toNumber(v: unknown): number | null {
  if (v == null || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const cleaned = String(v).replace(/\s|R\$/g, '').replace(/\./g, '').replace(',', '.')
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

function toStr(v: unknown): string {
  return v == null ? '' : String(v).trim()
}

export async function importProducts(opts: {
  file: File
  fieldMap: ImportFieldMap
  companyId: string
  source: 'csv' | 'xlsx'
}): Promise<ImportResult> {
  const { file, fieldMap, companyId, source } = opts

  const wb = await readWorkbook(file)
  const rows = firstSheetRows(wb)
  const result: ImportResult = { total: rows.length, inserted: 0, updated: 0, skipped: 0, errors: [] }

  // SKUs existentes + estado is_active de cada um.
  // is_active SEMPRE vai no payload com valor explícito (PostgREST determina colunas pelo
  // primeiro objeto do batch; valor ausente em outras linhas ficaria inconsistente).
  const { data: existing } = await supabase
    .from('products').select('sku, is_active').eq('company_id', companyId)
  const existingMap = new Map((existing ?? []).map(p => [p.sku, p.is_active as boolean]))
  const existingSet = new Set(existingMap.keys())

  // Cache de fornecedores (name → id) se mapeado
  const supplierCache = new Map<string, string | null>()
  async function resolveSupplier(name: string): Promise<string | null> {
    const key = name.toLowerCase()
    if (supplierCache.has(key)) return supplierCache.get(key) ?? null
    const { data } = await supabase
      .from('suppliers').select('id').eq('company_id', companyId)
      .ilike('name', `%${name}%`).limit(1).maybeSingle()
    const id = data?.id ?? null
    supplierCache.set(key, id)
    return id
  }

  type Payload = {
    company_id: string
    sku: string
    name: string
    current_stock: number
    cost?: number
    monthly_avg_manual?: number | null
    sale_price?: number | null
    supplier_id?: string | null
    is_active: boolean
    source: string
  }

  const batch: Payload[] = []
  const batchSkus: string[] = []  // para contagem de inserted vs updated

  async function flush() {
    if (batch.length === 0) return
    const { error } = await supabase
      .from('products').upsert(batch, { onConflict: 'company_id,sku' })
    if (error) {
      batchSkus.forEach(sku => result.errors.push({ row: 0, sku, reason: error.message }))
      result.skipped += batch.length
    } else {
      batchSkus.forEach(sku => {
        if (existingSet.has(sku)) result.updated++
        else result.inserted++
      })
    }
    batch.length = 0
    batchSkus.length = 0
  }

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    const sku = toStr(r[fieldMap.sku])
    const name = toStr(r[fieldMap.name])
    const stock = toNumber(r[fieldMap.current_stock])

    if (!sku) { result.skipped++; result.errors.push({ row: i + 2, sku: '', reason: 'SKU vazio' }); continue }
    if (!name) { result.skipped++; result.errors.push({ row: i + 2, sku, reason: 'Nome vazio' }); continue }
    if (stock == null) { result.skipped++; result.errors.push({ row: i + 2, sku, reason: 'Estoque inválido' }); continue }

    let supplierId: string | null | undefined
    if (fieldMap.supplier_name) {
      const supName = toStr(r[fieldMap.supplier_name])
      if (supName) supplierId = await resolveSupplier(supName)
    }

    // is_active: preserva valor existente; novos entram como ativo
    const isActive = existingMap.has(sku) ? (existingMap.get(sku) as boolean) : true

    const payload: Payload = {
      company_id: companyId,
      sku,
      name,
      current_stock: stock,
      is_active: isActive,
      source,
    }
    if (fieldMap.cost) {
      const v = toNumber(r[fieldMap.cost]); if (v != null) payload.cost = v
    }
    if (fieldMap.monthly_avg_manual) {
      payload.monthly_avg_manual = toNumber(r[fieldMap.monthly_avg_manual])
    }
    if (fieldMap.sale_price) {
      payload.sale_price = toNumber(r[fieldMap.sale_price])
    }
    if (supplierId !== undefined) payload.supplier_id = supplierId

    batch.push(payload)
    batchSkus.push(sku)

    if (batch.length >= BATCH_SIZE) await flush()
  }
  await flush()

  return result
}
